/**
 * UX guide overlay — centred modal with SVG icons and an optional
 * 5-second radial countdown on the close button.
 *
 * initGuide(container):
 *   - Shows the guide immediately (with 5-sec auto-close timer).
 *   - Adds a persistent help button (bottom-left of container) that
 *     re-opens the guide without a timer.
 */

// ─── SVG icon strings ────────────────────────────────────────────────────────

const ICON_ROTATE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
  <path d="M21.5 2v6h-6"/>
  <path d="M21.34 15.57a10 10 0 1 1-.57-8.38"/>
</svg>`;

const ICON_PAN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
  <polyline points="5 9 2 12 5 15"/>
  <polyline points="9 5 12 2 15 5"/>
  <polyline points="15 19 12 22 9 19"/>
  <polyline points="19 9 22 12 19 15"/>
  <line x1="2" y1="12" x2="22" y2="12"/>
  <line x1="12" y1="2" x2="12" y2="22"/>
</svg>`;

const ICON_ZOOM = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="11" cy="11" r="8"/>
  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  <line x1="11" y1="8" x2="11" y2="14"/>
  <line x1="8" y1="11" x2="14" y2="11"/>
</svg>`;

const ICON_FOCUS = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="9"/>
  <circle cx="12" cy="12" r="3"/>
  <line x1="12" y1="2" x2="12" y2="5.5"/>
  <line x1="12" y1="18.5" x2="12" y2="22"/>
  <line x1="2" y1="12" x2="5.5" y2="12"/>
  <line x1="18.5" y1="12" x2="22" y2="12"/>
</svg>`;

const ICON_HELP = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="10"/>
  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
  <line x1="12" y1="17" x2="12.01" y2="17"/>
</svg>`;

// ─── Styles (injected once) ──────────────────────────────────────────────────

let _stylesInjected = false;

function injectStyles(): void {
  if (_stylesInjected) return;
  _stylesInjected = true;

  // Countdown ring geometry: r=15 → circumference ≈ 94.25
  const CIRC = 2 * Math.PI * 15;

  const style = document.createElement('style');
  style.id = 'guide-styles';
  style.textContent = `
    /* ── Backdrop ── */
    .guide-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.45);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      z-index: 180;
      opacity: 1;
      transition: opacity 0.4s ease;
    }
    .guide-overlay--out {
      opacity: 0;
      pointer-events: none;
    }

    /* ── Modal card ── */
    .guide-modal {
      position: relative;
      width: min(480px, calc(100% - 48px));
      background: linear-gradient(160deg, rgba(18,20,30,0.97) 0%, rgba(10,12,20,0.97) 100%);
      border: 1px solid rgba(255,255,255,0.10);
      border-radius: 16px;
      padding: 40px 36px 36px;
      box-shadow: 0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04) inset;
      color: #e8eaf0;
    }

    /* ── Header ── */
    .guide-header {
      margin-bottom: 8px;
    }
    .guide-title {
      font: 700 17px/1.2 system-ui, -apple-system, sans-serif;
      letter-spacing: 0.02em;
      color: #fff;
      margin: 0 0 6px;
    }
    .guide-subtitle {
      font: 400 12px/1.4 system-ui, -apple-system, sans-serif;
      color: rgba(255,255,255,0.42);
      margin: 0;
      letter-spacing: 0.03em;
    }

    /* ── Divider ── */
    .guide-divider {
      border: none;
      border-top: 1px solid rgba(255,255,255,0.08);
      margin: 22px 0;
    }

    /* ── Hint grid ── */
    .guide-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .guide-hint {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      padding: 14px 16px;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.07);
      border-radius: 10px;
    }
    .guide-hint-icon {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      color: rgba(160,180,255,0.85);
      margin-top: 1px;
    }
    .guide-hint-icon svg {
      width: 100%;
      height: 100%;
    }
    .guide-hint-text {}
    .guide-hint-action {
      font: 600 13px/1.4 system-ui, -apple-system, sans-serif;
      color: #d8dce8;
      margin-bottom: 3px;
    }
    .guide-hint-desc {
      font: 400 11px/1.3 system-ui, -apple-system, sans-serif;
      color: rgba(255,255,255,0.45);
      letter-spacing: 0.03em;
    }

    /* ── Close button + countdown ring ── */
    .guide-close-wrap {
      position: absolute;
      top: 16px;
      right: 16px;
      width: 36px;
      height: 36px;
    }
    .guide-timer-svg {
      position: absolute;
      inset: -3px;
      width: calc(100% + 6px);
      height: calc(100% + 6px);
      transform: rotate(-90deg);
      pointer-events: none;
    }
    .guide-timer-track {
      fill: none;
      stroke: rgba(255,255,255,0.10);
      stroke-width: 2;
    }
    .guide-timer-fill {
      fill: none;
      stroke: rgba(140,160,255,0.75);
      stroke-width: 2;
      stroke-linecap: round;
      stroke-dasharray: ${CIRC.toFixed(2)};
      stroke-dashoffset: 0;
    }
    .guide-timer-fill--running {
      animation: guide-countdown 5s linear forwards;
    }
    @keyframes guide-countdown {
      from { stroke-dashoffset: 0; }
      to   { stroke-dashoffset: ${CIRC.toFixed(2)}; }
    }
    .guide-close-btn {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255,255,255,0.07);
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 50%;
      color: rgba(255,255,255,0.65);
      font-size: 13px;
      cursor: pointer;
      transition: background 0.15s, color 0.15s, border-color 0.15s;
    }
    .guide-close-btn:hover {
      background: rgba(255,255,255,0.14);
      color: #fff;
      border-color: rgba(255,255,255,0.25);
    }

    /* ── Persistent help button ── */
    .guide-help-btn {
      position: absolute;
      bottom: 16px;
      left: 16px;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(10,12,20,0.72);
      border: 1px solid rgba(255,255,255,0.13);
      border-radius: 50%;
      color: rgba(255,255,255,0.60);
      cursor: pointer;
      z-index: 160;
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      transition: background 0.15s, color 0.15s, border-color 0.15s;
      box-shadow: 0 2px 10px rgba(0,0,0,0.4);
    }
    .guide-help-btn:hover {
      background: rgba(30,34,54,0.90);
      color: #fff;
      border-color: rgba(255,255,255,0.25);
    }
    .guide-help-btn svg {
      width: 18px;
      height: 18px;
    }
  `;
  document.head.appendChild(style);
}

// ─── Hint data ───────────────────────────────────────────────────────────────

const HINTS: Array<{ icon: string; action: string; desc: string }> = [
  { icon: ICON_FOCUS,  action: 'Double-click',  desc: 'Focus on a region'  },
  { icon: ICON_PAN,    action: 'Right Drag',    desc: 'Pan view'            },
  { icon: ICON_ZOOM,   action: 'Scroll Wheel',  desc: 'Zoom in / out'      },
  { icon: ICON_ROTATE, action: 'Left Drag',     desc: 'Rotate model'       },
];

// ─── Build & show overlay ────────────────────────────────────────────────────

function buildOverlay(container: HTMLElement, withTimer: boolean, onClose?: () => void): void {
  // Remove any existing overlay first
  container.querySelector('.guide-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'guide-overlay';

  // ── Modal ──
  const modal = document.createElement('div');
  modal.className = 'guide-modal';

  // Close button + countdown ring
  const closeWrap = document.createElement('div');
  closeWrap.className = 'guide-close-wrap';

  const rSVG = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  rSVG.classList.add('guide-timer-svg');
  rSVG.setAttribute('viewBox', '0 0 42 42');

  const track = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  track.classList.add('guide-timer-track');
  track.setAttribute('cx', '21');
  track.setAttribute('cy', '21');
  track.setAttribute('r', '15');

  const fill = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  fill.classList.add('guide-timer-fill');
  fill.setAttribute('cx', '21');
  fill.setAttribute('cy', '21');
  fill.setAttribute('r', '15');

  rSVG.appendChild(track);
  rSVG.appendChild(fill);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'guide-close-btn';
  closeBtn.setAttribute('aria-label', 'Close guide');
  closeBtn.innerHTML = `<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" width="12" height="12"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>`;

  closeWrap.appendChild(rSVG);
  closeWrap.appendChild(closeBtn);
  modal.appendChild(closeWrap);

  // Header
  const header = document.createElement('div');
  header.className = 'guide-header';

  const title = document.createElement('h2');
  title.className = 'guide-title';
  title.textContent = 'Navigation Controls';

  const subtitle = document.createElement('p');
  subtitle.className = 'guide-subtitle';
  subtitle.textContent = 'Use the following gestures to explore the 3D viewer';

  header.appendChild(title);
  header.appendChild(subtitle);
  modal.appendChild(header);

  const divider = document.createElement('hr');
  divider.className = 'guide-divider';
  modal.appendChild(divider);

  // Hint grid
  const grid = document.createElement('div');
  grid.className = 'guide-grid';

  HINTS.forEach(({ icon, action, desc }) => {
    const hint = document.createElement('div');
    hint.className = 'guide-hint';

    const iconWrap = document.createElement('div');
    iconWrap.className = 'guide-hint-icon';
    iconWrap.innerHTML = icon;

    const text = document.createElement('div');
    text.className = 'guide-hint-text';

    const actionEl = document.createElement('div');
    actionEl.className = 'guide-hint-action';
    actionEl.textContent = action;

    const descEl = document.createElement('div');
    descEl.className = 'guide-hint-desc';
    descEl.textContent = desc;

    text.appendChild(descEl);
    text.appendChild(actionEl);
    hint.appendChild(iconWrap);
    hint.appendChild(text);
    grid.appendChild(hint);
  });

  modal.appendChild(grid);
  overlay.appendChild(modal);
  container.appendChild(overlay);

  // ── Dismiss logic ──
  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    overlay.classList.add('guide-overlay--out');
    overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
    onClose?.();
  };

  closeBtn.addEventListener('click', dismiss, { once: true });

  if (withTimer) {
    // Trigger reflow so the animation starts fresh, then add the running class
    void fill.getBoundingClientRect();
    fill.classList.add('guide-timer-fill--running');
    fill.addEventListener('animationend', dismiss, { once: true });
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Call once after the first model loads.
 * - Shows the guide immediately with a 5-second countdown.
 * - Appends a permanent help button (bottom-left) that reopens without timer.
 * - Calls `onFirstDismiss` once when the initial (timed) overlay is closed.
 */
export function initGuide(container: HTMLElement, onFirstDismiss?: () => void): void {
  injectStyles();

  // Show on first load with timer, firing callback on close
  buildOverlay(container, true, onFirstDismiss);

  // Persistent help button (re-opens without timer or callback)
  const helpBtn = document.createElement('button');
  helpBtn.className = 'guide-help-btn';
  helpBtn.setAttribute('aria-label', 'Show navigation guide');
  helpBtn.innerHTML = ICON_HELP;
  helpBtn.addEventListener('click', () => buildOverlay(container, false));
  container.appendChild(helpBtn);
}
